// SmartTrace API Connector - Real API Provider
// Ce fichier sera activé quand les credentials API seront fournis

const SMARTTRACE_API_URL = process.env.REACT_APP_SMARTTRACE_API_URL || '';
const SMARTTRACE_API_KEY = process.env.REACT_APP_SMARTTRACE_API_KEY || '';

/**
 * Recherche un lot via l'API SmartTrace réelle
 * @param {string} code - Le code du lot
 * @returns {Promise<Object|null>} - Les données du lot ou null
 */
export const getLotByCode = async (code) => {
  if (!SMARTTRACE_API_URL || !SMARTTRACE_API_KEY) {
    console.warn('SmartTrace API not configured. Using mock provider.');
    return null;
  }

  try {
    const response = await fetch(`${SMARTTRACE_API_URL}/lots/${code}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${SMARTTRACE_API_KEY}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      throw new Error(`SmartTrace API error: ${response.status}`);
    }

    const data = await response.json();
    return transformSmartTraceResponse(data);
  } catch (error) {
    console.error('SmartTrace API error:', error);
    throw error;
  }
};

/**
 * Vérifie si un lot existe via l'API
 * @param {string} code - Le code du lot
 * @returns {Promise<boolean>}
 */
export const lotExists = async (code) => {
  try {
    const lot = await getLotByCode(code);
    return lot !== null;
  } catch {
    return false;
  }
};

/**
 * Transforme la réponse SmartTrace au format interne
 * @param {Object} apiResponse - Réponse brute de l'API
 * @returns {Object} - Données formatées
 */
const transformSmartTraceResponse = (apiResponse) => {
  // Adapter selon le format réel de l'API SmartTrace
  // Cette fonction sera complétée quand l'API sera documentée
  return {
    code: apiResponse.lot_code || apiResponse.code,
    status: apiResponse.status || 'verified',
    product: {
      name: {
        fr: apiResponse.product_name_fr || apiResponse.product_name,
        en: apiResponse.product_name_en || apiResponse.product_name
      },
      variety: apiResponse.variety,
      category: {
        fr: apiResponse.category_fr || apiResponse.category,
        en: apiResponse.category_en || apiResponse.category
      },
      image: apiResponse.product_image
    },
    origin: {
      country: {
        fr: apiResponse.origin_country_fr || apiResponse.origin_country,
        en: apiResponse.origin_country_en || apiResponse.origin_country
      },
      region: apiResponse.origin_region,
      producer: apiResponse.producer_name,
      harvestDate: apiResponse.harvest_date
    },
    journey: (apiResponse.journey || []).map((step, index) => ({
      step: index + 1,
      location: {
        fr: step.location_fr || step.location,
        en: step.location_en || step.location
      },
      date: step.date,
      temperature: step.temperature,
      status: step.status || 'completed'
    })),
    certifications: apiResponse.certifications || [],
    qualityScore: apiResponse.quality_score,
    expiryDate: apiResponse.expiry_date,
    packaging: {
      fr: apiResponse.packaging_fr || apiResponse.packaging,
      en: apiResponse.packaging_en || apiResponse.packaging
    },
    // Champs spécifiques au rappel
    recallReason: apiResponse.recall_reason ? {
      fr: apiResponse.recall_reason_fr || apiResponse.recall_reason,
      en: apiResponse.recall_reason_en || apiResponse.recall_reason
    } : undefined,
    recallDate: apiResponse.recall_date
  };
};

/**
 * Retourne un exemple de code (pour l'interface)
 * @returns {string}
 */
export const getExampleCode = () => 'OCN-MK-2024-001';

export default {
  getLotByCode,
  lotExists,
  getExampleCode
};
